import React, { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { useNavigate, useLocation } from 'react-router-dom';
import { api } from '../lib/api.ts';
import { useBusyActions } from '../lib/useBusyActions.ts';
import { categoryMatches } from '../lib/categoryTree.ts';
import { isLibraryItemSlug } from '../lib/format.ts';
import { caseStudyCategory } from '../../shared/library.ts';
import ThemeToggle from '../components/ThemeToggle.tsx';
import AppHeader from '@shared-client/AppHeader.tsx';
import BuildsIndicator from '../components/BuildsIndicator.tsx';
import { useTileSize } from '../components/TileSizeControl.tsx';
import { useCoverWarm } from '../lib/coverWarm.ts';
import { useBuilds, refreshBuilds } from '../lib/useBuilds.ts';
import CategorySidebar from '../components/CategorySidebar.tsx';
import ManageCategoriesModal from '../components/ManageCategoriesModal.tsx';
import MoveToCategoryModal from '../components/MoveToCategoryModal.tsx';
import SettingsDrawer from '../canvas/SettingsDrawer.tsx';
import ConfirmModal from '../components/ConfirmModal.tsx';
import Toast from '../components/Toast.tsx';
import BankLibrary from '../components/bank/BankLibrary.tsx';
import AddSlideChooser from '../canvas/AddSlideChooser.tsx';
import { warmGallery } from '../lib/gallery.ts';
import SlideEditorOverlay from '../library/SlideEditorOverlay.tsx';
import ImportCaseStudyModal from '../library/ImportCaseStudyModal.tsx';
import InsertIntoDeckModal from '../library/InsertIntoDeckModal.tsx';
import SlidePreviewModal from '../components/SlidePreviewModal.tsx';
import { useToast } from '../lib/useToast.ts';
import { errorMessage } from '../lib/api.ts';
import { Plus, Settings } from '../lib/icons.tsx';
import { type ZoomTarget } from './library/LibraryTile.tsx';
import LibraryBody from './library/LibraryBody.tsx';
import LibraryEmptyState from './library/LibraryEmptyState.tsx';
import LibraryRailNote from './library/LibraryRailNote.tsx';
import { libraryViewRows } from './library/libraryViews.ts';
import { type LibraryItemActions } from './library/LibraryItemMenu.tsx';
import { UNCATEGORISED, useLibraryFilters } from './library/useLibraryFilters.ts';
import { useLibraryFilterState } from './library/useLibraryFilterState.ts';
import {
  libraryCountsLine,
  libraryFilterBarModel,
  libraryFilterChips,
  libraryShowingLine,
} from './library/libraryFilterOptions.ts';
import LibraryFilterBar from './library/LibraryFilterBar.tsx';
import LibraryTabs from './library/LibraryTabs.tsx';
import DeckTitleRow from './dashboard/DeckTitleRow.tsx';
import DeckSearchBar from './dashboard/DeckSearchBar.tsx';
import FilterChipRow from './dashboard/FilterChipRow.tsx';
import type { ReactNode } from 'react';
import type { TileSize } from '../components/TileSizeControl.tsx';
import type { TemplatePick, UploadedSlide } from '../editor/useDeckEditor.ts';
import type {
  BankPayload,
  CategoryColour,
  CategoryEntry,
  LibraryItem,
  LibraryPayload,
  Person,
  SlideFromUploadPayload,
} from '../../shared/api.ts';
import type { DeckSummary } from '../../shared/deck.ts';
import AccountMenu from '../components/AccountMenu.tsx';

// The RKH Library home (/library): the one place the shared shelves live.
//
// THE MODEL: the library is a shelf of ITEMS, and an item OWNS A DECK
// (`lib-<name>`, server: pipeline/library-items.ts; see docs/library.md). An
// item is self-contained (one slide, or an ordered series: a 3-slide case
// study, a who-we-are sequence), and inserting one into a deck brings exactly
// its slides, as a snapshot. Its slug is allocated once, at creation, and never
// moves on rename: nothing here derives one from a name.
// CATEGORIES are the item's browsing home ("Team", "Case studies") — a 1–2
// segment Parent/Child path, mirroring the decks dashboard: a left sidebar
// tree + a Manage Categories modal, deep-linkable via ?cat= (the Slides tab's
// name for it; ?category= is the BANK tab's, and the old Slides links carrying
// it are still read). Since Run 9 the whole view — search, type, category,
// usage, grouping and sort — is one object mirrored into the URL:
// library/libraryFilterState.ts.
// Storage: the item's name is its deck's title, its shelf label is on its own
// studio.json, and the shelf registry (order and colour, the one thing
// belonging to no item) is .studio-state/library-categories.json. The
// built-in "Case studies" shelf (shared/library.ts) is where the case-study
// importer files, so it cannot be renamed or deleted.
//
// Until 2026-08-21 every item was a SECTION of one `rkh-library` deck. That
// derived self-containment rather than storing it, and the shelf's coordinates
// leaked into every item-scoped surface.
//
// Slides mode shows the items of a category as cards (cached cover tile,
// "N slides" badge on a series); clicking a card edits the item in place via
// the full chat stack (library/SlideEditorOverlay.tsx). Bank mode is the RKH
// asset bank manager (components/bank/BankLibrary.tsx).
//
// Item decks are hidden from the decks dashboard on purpose: they aren't
// presentations, and surfacing them there confused everyone. This page is
// the only home.

/** How often to re-read the shelf while a build is writing into it. Matches the
 *  build list's own poll, since that is the fastest anything here can learn a
 *  build has finished anyway. */
const SHELF_POLL_MS = 3000;

// Card-size steps — the grid's minmax() minimum (see .library__grid).
const TILE_MIN: Record<TileSize, string> = { sm: '240px', md: '300px', lg: '380px' };

/** The page's one fetch, held as a single object so a failed load can't leave
 *  a stale item list beside an error. */
type LibraryState = {
  status: 'loading' | 'ready' | 'error';
  /** Each item carries its own slides. */
  items: LibraryItem[];
  /** The category registry in display order, carrying each category's optional
   *  colour — the rail's folder dots and the grouped body's heading dots. */
  categoryOrder: CategoryEntry[];
  /** Carried by the listing itself, so a card can name its owner without a
   *  second request. */
  people: Person[];
  error: string | null;
};

/** What a pending confirmation carries; the modal's own cancel/busy are
 *  supplied at the render site. */
type PendingConfirm = {
  title: ReactNode;
  body?: ReactNode;
  danger?: boolean;
  confirmLabel?: string;
  onConfirm: () => void;
};

export default function Library() {
  const navigate = useNavigate();
  const location = useLocation();
  const bankMode = location.pathname.endsWith('/bank');
  const [tileSize, setTileSize] = useTileSize('studio.libraryTileSize');
  const [showSettings, setShowSettings] = useState(false);
  const [state, setState] = useState<LibraryState>({
    status: 'loading',
    items: [],
    categoryOrder: [],
    people: [],
    error: null,
  });
  const [editingItem, setEditingItem] = useState<string | null>(null); // item slug being edited in the overlay
  const [newItemOpen, setNewItemOpen] = useState(false); // the "+ New item" modal
  const [importOpen, setImportOpen] = useState(false); // the "Import a case study" modal
  const [addingTo, setAddingTo] = useState<string | null>(null); // item slug receiving slides via the chooser
  const [openMenuKey, setOpenMenuKey] = useState<string | null>(null);
  const [confirm, setConfirm] = useState<PendingConfirm | null>(null);
  const [manageCats, setManageCats] = useState(false);
  const [moveItem, setMoveItem] = useState<LibraryItem | null>(null); // filed via MoveToCategoryModal
  // The item waiting for a deck to be inserted into. Held here rather than in
  // the card, so only one picker can ever be open.
  const [insertItem, setInsertItem] = useState<LibraryItem | null>(null);
  const { busy, run } = useBusyActions();
  // slug -> cover URL, for covers the warm queue rendered after the listing
  // loaded. Held apart from `state.items` so the next load() cannot throw one
  // away: the listing only ever reports covers that are already CACHED, so
  // merging the two can only ever add. The library used to mount one LIVE
  // iframe per item: a whole deck shell each, all at once with no lazy shelf,
  // holding a Vite child from a pool only four wide. Images cost none of that;
  // the magnifier covers actually reading a slide.
  const [covers, setCovers] = useState<Record<string, string>>({});
  // An item's first slide, opened big in a live frame.
  const [zoomSlide, setZoomSlide] = useState<ZoomTarget | null>(null);
  const { toast, pushToast, runUndo, dismissToast } = useToast();
  // How many assets the Bank shelf holds, for its tab's count. A tiny request
  // of its own on mount, rather than only the number `BankLibrary` reports:
  // that component mounts on the other tab, so a count taken from it alone
  // would be missing exactly when the tab is asking to be clicked. It DOES
  // report too, so the count keeps up with uploads and archives while the Bank
  // is open. null until either answers.
  const [bankCount, setBankCount] = useState<number | null>(null);
  // The Bank's free-text filter lives here, not in BankLibrary, because the
  // field that drives it is the page's own search bar under the title.
  const [bankQuery, setBankQuery] = useState('');
  /** Item slugs whose slides are still being written (a case-study import),
   *  read off the SERVER's own job list rather than remembered here. A local
   *  flag did not survive a reload or a walk to another page, so a tile went
   *  back to offering "Add the first slide" on an item a build was still
   *  writing into (Alex, 2026-08-19). A build's slug IS the item. */
  const builds = useBuilds();
  /** Item slugs being built, sorted and joined. A string rather than an array
   *  so it is stable across polls: the list is rebuilt every three seconds, and
   *  the effect below has to fire when a build starts or ends, not every tick. */
  const buildingSlugs = useMemo(
    () =>
      (builds.builds || [])
        .filter((b) => isLibraryItemSlug(b.slug) && (b.status === 'running' || b.status === 'paused'))
        .map((b) => b.slug)
        .sort()
        .join('\n'),
    [builds.builds],
  );
  const building = useMemo(() => (buildingSlugs ? buildingSlugs.split('\n') : []), [buildingSlugs]);
  /** Items whose build FAILED, and what it said. Bounded by the job registry's
   *  30-minute reap, so this answers "I did that just now and nothing happened"
   *  and not "what happened last Tuesday". */
  const failedBuilds = useMemo(() => {
    const byItem = new Map<string, string>();
    for (const b of builds.builds || []) {
      if (!isLibraryItemSlug(b.slug) || b.status !== 'failed') continue;
      byItem.set(b.slug, b.error || 'The build failed without saying why.');
    }
    return byItem;
  }, [builds.builds]);
  // The Slides tab's whole view, mirrored into the URL. Inert in bank mode —
  // `BankLibrary` owns `?category=` over there (see the hook).
  const { filters, patch, setQuery, setType, setCategory, setUsed, setGroup, setSort, clearAxis, clearFilters } =
    useLibraryFilterState(!bankMode);

  const load = useCallback(async () => {
    try {
      const data = await api.get<LibraryPayload>('/api/library');
      // `categories` is the registry WITH its colours; `categoryOrder` is the
      // same list as bare paths, kept on the wire for anything still reading
      // it. A client cached across the deploy that lands on the old payload
      // falls back to the paths, which is the untinted rail it already had.
      setState({
        status: 'ready',
        items: data.items || [],
        categoryOrder:
          data.categories ||
          (data.categoryOrder || []).map((path) => ({ path, colour: null, hidden: false, isDefault: false })),
        people: data.people || [],
        error: null,
      });
    } catch (err) {
      setState({ status: 'error', items: [], categoryOrder: [], people: [], error: errorMessage(err) });
    }
  }, []);
  useEffect(() => {
    load();
  }, [load]);

  // The Bank tab's count. Best-effort: a failed read leaves the tab countless
  // rather than putting an error on a page whose own shelf loaded fine.
  useEffect(() => {
    let cancelled = false;
    api
      .get<BankPayload>('/api/bank')
      .then((res) => {
        if (!cancelled) setBankCount((res.items || []).length);
      })
      .catch(() => {
        /* no count on this tab, which is what null means */
      });
    return () => {
      cancelled = true;
    };
  }, []);

  async function logout() {
    await api.post('/api/logout');
    window.location.href = '/login';
  }

  // Click-away and Escape for the open kebab menu live in the menu itself
  // (library/LibraryItemMenu.tsx), which is what holds the refs.

  // Warm the "Add a slide" gallery, as the editor does on mount. Only the
  // editor did, so opening the chooser from HERE paid the whole cold start —
  // the Vite child spawn AND the module-graph transform — with the modal
  // already on screen showing a grey panel.
  useEffect(() => {
    warmGallery();
  }, []);

  const refresh = useCallback(() => load(), [load]);

  // A build changes the shelf on the server and nothing pushes a word of it: the
  // item appears a moment AFTER the import request returns, its slides land one
  // at a time, and the item is only whole once the job goes. Read the shelf
  // again for as long as one is writing into it, and once more when it stops;
  // otherwise the tile keeps whatever the page happened to load at the start,
  // which is an empty item offering "Add the first slide" on a case study that
  // is by then written, until someone reloads (Alex, 2026-08-19).
  const watched = useRef<string>('');
  useEffect(() => {
    const before = watched.current;
    watched.current = buildingSlugs;
    if (before && before !== buildingSlugs) void refresh();
    if (!buildingSlugs) return;
    const t = setInterval(() => void refresh(), SHELF_POLL_MS);
    return () => clearInterval(t);
  }, [buildingSlugs, refresh]);

  // Covers come off the listing already cached, and anything missing is warmed
  // one deck at a time as it scrolls into view. This is the DASHBOARD's queue,
  // not the whole-deck thumb pass the shelf used to use, and the swap is the
  // reason one deck per item is safe: the old shape asked ONE deck for N
  // thumbnails, which is what that pass is good at. With an item per deck the
  // same call becomes N decks for one thumbnail each, the exact pathology
  // slide-thumbs.ts records. useCoverWarm is built for N decks: serial,
  // demand-shrinking, defers on `busy` and gives up terminally.
  const onCover = useCallback((slug: string, url: string) => setCovers((cur) => ({ ...cur, [slug]: url })), []);
  const { warmingNow, coverStalled, retryCover, coverRef } = useCoverWarm({ enabled: true, onCover });

  // ---- item creation + growth ---------------------------------------------

  // The server allocates the slug and the accent: it is the only side that can
  // see every item, and a client-side guess would race a concurrent create.
  async function onCreateItem(name: string, category: string) {
    await run(async () => {
      const made = await api.post<{ slug: string }>('/api/library/items', { name, category });
      setNewItemOpen(false);
      await load();
      setCategory(category);
      // Straight into picking the item's first slide.
      setAddingTo(made.slug);
    }, 'Could not create the item');
  }

  /** Growing an item from the card menu appends to its LAST section. Most
   *  items have exactly one; a split case study appends to the end of the run. */
  function appendSectionFor(itemSlug: string): string | undefined {
    return state.items.find((i) => i.slug === itemSlug)?.sections.at(-1)?.key;
  }

  // A gallery pick for the item being grown (a template, an existing library
  // slide, or a slide borrowed from any other deck — the inverse of "Save to
  // library"), inserted into the item's own deck. A library pick and an
  // other-deck pick are the same call: an item IS a deck, so there is nothing
  // left for a separate from-library route to do. The chooser stays open so a
  // series can be built up in one sitting.
  async function handlePickForItem(pick: TemplatePick) {
    if (!addingTo) return;
    return run(async () => {
      const section = appendSectionFor(addingTo);
      const res = pick.fromSlug
        ? await api.post(`/api/slides/${addingTo}/slide/from-deck`, {
            fromSlug: pick.fromSlug,
            slideId: pick.slideId,
            section,
          })
        : await api.post(`/api/slides/${addingTo}/slide/from-template`, {
            template: pick.pathTail || pick.componentName,
            ...(pick.variant ? { variant: pick.variant } : {}),
            section,
          });
      pushToast(`Added "${pick.title}": add another, or close the picker`);
      await refresh();
      return res;
    }, 'Could not add the slide');
  }

  // "Upload a finished slide" into a library item — the custom case-study
  // path. Same from-upload route as decks; an item's deck IS an ordinary deck.
  async function handleUploadForItem({ blob, filename, title, chrome, fit }: UploadedSlide) {
    if (!addingTo) return;
    await run(async () => {
      const form = new FormData();
      form.append('file', blob, filename);
      if (title) form.append('title', title);
      if (!chrome) form.append('chrome', '0');
      if (fit === 'contain') form.append('fit', 'contain');
      const section = appendSectionFor(addingTo);
      if (section) form.append('section', section);
      const data = await api.postForm<SlideFromUploadPayload>(`/api/slides/${addingTo}/slide/from-upload`, form);
      pushToast(`Added "${data.title}": add another, or close the picker`);
      await refresh();
    }, 'Could not add the slide');
  }

  // ---- item actions --------------------------------------------------------

  async function onRenameItem(item: LibraryItem) {
    const next = window.prompt('Rename item', item.name);
    if (!next || !next.trim() || next.trim() === item.name) return;
    await run(async () => {
      // The name is the item deck's title; a single-section item IS its
      // section, so the server renames that too. The slug never moves.
      await api.put(`/api/library/items/${item.slug}/name`, { name: next.trim() });
      await refresh();
    }, 'Rename failed');
  }

  // Move an item into a category (its browsing home). `category` is a 1–2
  // segment path or null/'' (falls back to the default Library shelf).
  async function saveItemCategory(item: LibraryItem, category: string | null) {
    await run(async () => {
      await api.put(`/api/library/items/${item.slug}/category`, { category: category || '' });
      setMoveItem(null);
      await load();
      pushToast(`Moved "${item.name}" to ${category || 'Library'}`);
    }, 'Move failed');
  }

  // Insert a whole item into a deck the user picks. Same endpoint the editor's
  // Add-a-slide gallery calls for a suite insert, so an item lands identically
  // whichever side of the app asked for it.
  async function insertIntoDeck(item: LibraryItem, deck: DeckSummary) {
    await run(async () => {
      const res = await api.post<{ count?: number }>(`/api/slides/${deck.slug}/section/from-library`, {
        itemSlug: item.slug,
      });
      setInsertItem(null);
      // No navigation: whoever is browsing the library may well insert into a
      // second deck next, and yanking them into the editor would lose that. The
      // way in is offered instead of taken.
      pushToast(`Inserted "${item.name}" (${res.count ?? item.count} slides) into "${deck.title}"`, {
        actionLabel: 'Open deck',
        undo: () => navigate(`/editor/${deck.slug}`),
      });
    }, 'Could not insert the item');
  }

  async function onRecolourItem(item: LibraryItem, color: string) {
    await run(async () => {
      // Recolours every section of the item's deck.
      await api.put(`/api/library/items/${item.slug}/color`, { color });
      await refresh();
    }, 'Recolour failed');
  }

  function onDeleteItem(item: LibraryItem) {
    setConfirm({
      title: `Remove "${item.name}" from the library?`,
      body: `Its ${item.count} slide${item.count === 1 ? '' : 's'} will no longer be offered in the Add-a-slide gallery, though it can be restored from Trash on the decks page. Decks that already inserted ${item.count === 1 ? 'it' : 'them'} keep their copies.`,
      danger: true,
      confirmLabel: 'Remove item',
      onConfirm: async () => {
        await run(async () => {
          // A soft delete: the item's deck moves to Trash and can be restored.
          await api.delete(`/api/library/items/${item.slug}`);
          await refresh();
        }, 'Delete failed');
        setConfirm(null);
      },
    });
  }

  // ---- category management (Manage Categories modal) ----------------------
  async function onCreateCategory(path: string) {
    await run(async () => {
      await api.post('/api/library/categories/create', { path });
      await load();
    }, 'Creating category failed');
  }

  async function onRenameCategoryPath(from: string, to: string) {
    await run(async () => {
      await api.put('/api/library/categories/rename', { from, to });
      await load();
    }, 'Renaming category failed');
  }

  // Tint a category, or clear its tint. The rail and the group headings read
  // the colour off the tree, so a reload is what makes the change visible —
  // the same shape as the decks page's `onSetCategoryColour`.
  async function onSetCategoryColour(path: string, colour: CategoryColour | null) {
    await run(async () => {
      await api.put('/api/library/categories/colour', { path, colour });
      await load();
    }, 'Setting category colour failed');
  }

  async function onReorderCategories(order: string[]) {
    await run(async () => {
      await api.put('/api/library/categories/order', { order });
      await load();
    }, 'Reordering categories failed');
  }

  function onDeleteCategoryPath(path: string) {
    const p = path.toLowerCase();
    const count = state.items.filter((it) => {
      const c = (it.category || '').toLowerCase();
      return c === p || c.startsWith(p + '/');
    }).length;
    setConfirm({
      title: `Delete the "${path}" category?`,
      body: `It will be removed from ${count} item${count === 1 ? '' : 's'} (and any sub-categories). The items aren't deleted: they move to the default Library shelf.`,
      danger: true,
      confirmLabel: 'Delete category',
      onConfirm: async () => {
        await run(async () => {
          await api.post('/api/library/categories/delete', { path });
          await load();
          // Leaving a now-deleted view: fall back to All.
          if (filters.category && filters.category !== UNCATEGORISED && categoryMatches(filters.category, path)) {
            setCategory(null);
          }
        }, 'Deleting category failed');
        setConfirm(null);
      },
    });
  }

  // The /library?category= deep-link for a category (applies the filter).
  function onCopyFilterLink(path: string) {
    // `cat`, not `category`: that name is the BANK tab's on /library/bank
    // (libraryFilterState.ts). Links written before this still work — the codec
    // reads the old name and normalises it on arrival.
    const url = `${window.location.origin}/library?cat=${encodeURIComponent(path)}`;
    navigator.clipboard.writeText(url).catch(() => window.alert('Copy failed'));
  }

  // ---- derived data -------------------------------------------------------
  const { categoryTree, allCategories, counts, slideText, filtering, filteredItems, editing, editingFirstSlide } =
    useLibraryFilters({
      items: state.items,
      categoryOrder: state.categoryOrder,
      filters,
      editingItem,
    });
  const chips = libraryFilterChips(filters);
  // The rail's Views section, derived beside the other pure modules so the two
  // halves that are easy to get wrong — which row is lit, and what each count
  // means — are testable without a DOM. Both counted rows are ZERO until decks
  // start taking slides from the shelf; `libraryEmptyState.ts` is what stops
  // that reading as a fault.
  const viewRows = libraryViewRows({ items: state.items, filters });

  // "How saving works", promoted out of the intro line's `title=` tooltip. It
  // is an EXPLAINER, not a new save flow: items are saved from a deck's slide
  // menu, so the only thing the button can honestly do is say where that is and
  // offer to take you there. (Run 10 gives the same words a rail panel.)
  function explainSaving(category?: string | null) {
    setConfirm({
      title: 'How saving works',
      body: (
        <>
          <p className="muted small">
            Library items are saved FROM a deck, not started here. Open any deck, then on the slide's ⋮ menu choose
            "Save a copy to the RKH library". Tick several slides first and they are saved together as one series.
          </p>
          {category && (
            <p className="muted small">
              The save dialog asks which category the item belongs in. Pick {category} there and it lands under that
              heading.
            </p>
          )}
          <p className="muted small">
            What you save is a snapshot. Editing it here never changes the deck it came from, and never changes a deck
            that has already inserted it.
          </p>
        </>
      ),
      confirmLabel: 'Go to decks',
      onConfirm: () => navigate('/'),
    });
  }

  // The item verbs, as one object: the tile passes them to the kebab menu and
  // never learns what any of them do. Insert opens the deck picker: an insert
  // needs a deck to insert INTO, and this page has none until it is asked.
  const itemActions: LibraryItemActions = {
    onEdit: (item) => setEditingItem(item.slug),
    onInsert: (item) => setInsertItem(item),
    onAddSlide: (item) => setAddingTo(item.slug),
    onRename: onRenameItem,
    onMove: (item) => setMoveItem(item),
    onRecolour: onRecolourItem,
    onDelete: onDeleteItem,
  };

  // The same five cover affordances the page holds, handed to every tile. The
  // listing's cached cover wins over one the queue rendered earlier: after an
  // edit it is the listing that knows the newer picture.
  const tileThumbs = {
    urlFor: (item: LibraryItem) => item.cover ?? covers[item.slug] ?? null,
    observe: coverRef,
    coming: warmingNow,
    isStuck: coverStalled,
    retry: retryCover,
  };

  return (
    <div className="library">
      <AppHeader
        active="library"
        onNavigate={navigate}
        actions={[
          { key: 'new', label: 'New deck', primary: true, onSelect: () => navigate('/new') },
          { key: 'builds', label: 'Builds', render: () => <BuildsIndicator /> },
          {
            key: 'theme',
            label: 'Appearance',
            ownRow: true,
            render: ({ inSheet }) => <ThemeToggle inSheet={inSheet} />,
          },
          {
            key: 'settings',
            label: 'Settings',
            // An ICON in the bar since 2026-08-07, so it takes the square icon
            // wrapper rather than `.btn`'s text pill, which rendered it 39x31.
            barLabel: <Settings />,
            barClassName: 'iconbtn',
            hint: 'Models, engines, storage',
            onSelect: () => setShowSettings(true),
          },
          {
            key: 'account',
            label: 'Sign out',
            ownRow: true,
            render: ({ inSheet }) => <AccountMenu inSheet={inSheet} onSignOut={logout} />,
          },
        ]}
      />
      {showSettings && <SettingsDrawer onClose={() => setShowSettings(false)} />}

      <main className="library__body">
        {/* The top of the page, in the decks page's language: title row, tabs,
            search, pills, chips. The title row and the tabs render on BOTH
            shelves — they are the page's identity and its one axis — and
            everything below them is the Slides tab's, because it reads a
            Slides-mode filter state the Bank tab does not have. */}
        <div className="library__top">
          <DeckTitleRow
            title="Library"
            line={
              bankMode
                ? bankCount === null
                  ? ''
                  : `${bankCount} shared ${bankCount === 1 ? 'asset' : 'assets'}`
                : libraryCountsLine(counts)
            }
            actions={
              bankMode ? undefined : (
                <>
                  <button type="button" className="btn btn--ghost btn--sm" onClick={() => explainSaving()}>
                    Save from a deck
                  </button>
                  <button
                    type="button"
                    className="btn btn--primary btn--sm"
                    onClick={() => setNewItemOpen(true)}
                    disabled={busy}
                  >
                    <Plus /> New item
                  </button>
                </>
              )
            }
          />
          <LibraryTabs
            bankMode={bankMode}
            slidesCount={counts.items}
            bankCount={bankCount}
            onSelect={(path) => navigate(path)}
          />
          {bankMode && (
            <DeckSearchBar
              query={bankQuery}
              onQuery={setBankQuery}
              // The Bank filters itself and does not report back how many
              // matched, so the settled announcement is skipped rather than
              // guessed.
              resultCount={bankCount ?? 0}
              totalCount={bankCount ?? 0}
              label="Search the bank"
              placeholder="Search the bank: name, role, tag…"
              inputId="bank-search-input"
              announce={() => ''}
            />
          )}
          {!bankMode && state.status === 'ready' && state.items.length > 0 && (
            <>
              <DeckSearchBar
                query={filters.query}
                onQuery={setQuery}
                resultCount={filteredItems.length}
                totalCount={state.items.length}
                label="Search the library"
                placeholder="Search items by name, category or slide title…"
                inputId="library-search-input"
                announce={libraryShowingLine}
              />
              <LibraryFilterBar
                model={libraryFilterBarModel({
                  items: state.items,
                  filters,
                  slideText,
                  categoryTree,
                })}
                onType={setType}
                onCategory={setCategory}
                onUsed={setUsed}
                onGroup={setGroup}
                onSort={setSort}
                tileSize={tileSize}
                onTileSize={setTileSize}
              />
              <FilterChipRow
                chips={chips}
                showing={libraryShowingLine(filteredItems.length, state.items.length)}
                onClearAxis={clearAxis}
                onClearAll={clearFilters}
              />
            </>
          )}
        </div>

        {bankMode ? (
          <BankLibrary onCount={setBankCount} query={bankQuery} onQuery={setBankQuery} />
        ) : (
          <>
            {/* The `title=` tooltip that used to hang off this line is now the
                rail's "How saving works" panel — see LibraryRailNote. A
                tooltip is not there when you go looking for it. */}
            <p className="muted small library__intro">
              Click an item to edit it: inserts are snapshots, so edits here never change decks that already use it.
            </p>
            {/* A failed cover carries its own way back (the retry control on
                the tile itself), so there is no page-level thumbnail note. The
                render cap the old shelf deck could hit is gone with it: an item
                owns a deck, and no item approaches it. */}
            {state.status === 'loading' && <p className="muted">Loading library…</p>}
            {state.status === 'error' && (
              <div className="dashboard__error">
                <strong>Couldn't load the library</strong>
                <p className="muted">{state.error}</p>
                <button type="button" className="btn btn--ghost btn--sm" onClick={load}>
                  Retry
                </button>
              </div>
            )}
            {state.status === 'ready' && state.items.length === 0 && (
              <div className="library__empty">
                <p className="muted">
                  Nothing here yet. Create your first item, or save a slide from any deck (tile ⋮ → "Save a copy to the
                  RKH library").
                </p>
                <button type="button" className="btn btn--primary" onClick={() => setNewItemOpen(true)} disabled={busy}>
                  <Plus /> New item
                </button>
              </div>
            )}
            {state.status === 'ready' && state.items.length > 0 && (
              <>
                <div className="dashboard__layout">
                  <CategorySidebar
                    tree={categoryTree}
                    active={filters.category}
                    allCount={state.items.length}
                    uncategorisedCount={0}
                    uncategorisedKey={UNCATEGORISED}
                    onSelect={setCategory}
                    onManage={() => setManageCats(true)}
                    ariaLabel="Library views and categories"
                    manageLabel="Manage categories"
                    views={viewRows}
                    onSelectView={(id) => {
                      const row = viewRows.find((v) => v.id === id);
                      if (row) patch(row.patch);
                    }}
                    foldersLabel="Categories"
                    // The design's rail panel, promoted out of the intro
                    // line's `title=` tooltip: a tooltip is not there when you
                    // are looking for it and never exists at all on a phone.
                    footer={<LibraryRailNote />}
                  />
                  <div className="dashboard__main">
                    {filteredItems.length === 0 ? (
                      <LibraryEmptyState
                        filters={filters}
                        onClearFilters={clearFilters}
                        onNewItem={() => setNewItemOpen(true)}
                      />
                    ) : (
                      <LibraryBody
                        items={filteredItems}
                        people={state.people}
                        tree={categoryTree}
                        grouped={filters.group === 'category'}
                        filtering={filtering}
                        building={building}
                        failures={failedBuilds}
                        busy={busy}
                        openMenuKey={openMenuKey}
                        setMenuKey={setOpenMenuKey}
                        actions={itemActions}
                        onZoom={setZoomSlide}
                        thumbs={tileThumbs}
                        tileMin={TILE_MIN[tileSize]}
                        onNewItem={() => setNewItemOpen(true)}
                        onSaveHere={(category) => explainSaving(category)}
                        onImportCaseStudy={() => setImportOpen(true)}
                      />
                    )}
                  </div>
                </div>
              </>
            )}
          </>
        )}
      </main>

      <Toast toast={toast} onUndo={runUndo} onDismiss={dismissToast} />
      {confirm && <ConfirmModal {...confirm} busy={busy} onCancel={() => setConfirm(null)} />}
      {manageCats && (
        <ManageCategoriesModal
          tree={categoryTree}
          busy={busy}
          // The shelf the importer files onto: padlocked here and refused
          // server-side. Its sub-categories stay editable, as the Bank's do.
          lockedPaths={[caseStudyCategory()]}
          onClose={() => setManageCats(false)}
          onCreate={onCreateCategory}
          onRename={onRenameCategoryPath}
          onDelete={onDeleteCategoryPath}
          onReorder={onReorderCategories}
          onSetColour={onSetCategoryColour}
          onCopyFilterLink={onCopyFilterLink}
        />
      )}
      {moveItem && (
        <MoveToCategoryModal
          deck={{ category: moveItem.category, title: moveItem.name }}
          allCategories={allCategories}
          busy={busy}
          moveLabel="Move item"
          clearLabel="Reset to Library"
          onCancel={() => setMoveItem(null)}
          // Narrowed here rather than in the modal: without `multi` it is in
          // single-category mode, which is the only mode a library item has —
          // the array arm belongs to the decks dashboard.
          onSave={(category) => saveItemCategory(moveItem, Array.isArray(category) ? (category[0] ?? null) : category)}
        />
      )}
      {insertItem && (
        <InsertIntoDeckModal
          item={insertItem}
          busy={busy}
          onClose={() => setInsertItem(null)}
          onInsert={(deck) => insertIntoDeck(insertItem, deck)}
        />
      )}
      {importOpen && (
        <ImportCaseStudyModal
          onCancel={() => setImportOpen(false)}
          onStarted={async () => {
            setImportOpen(false);
            // The job list FIRST, then the shelf. The item exists already, so
            // it is on the shelf before its first slide is written; draw it
            // before the build is known and its tile offers "Add the first
            // slide" on a build that is already writing into it.
            await refreshBuilds({ fresh: true });
            void refresh();
          }}
        />
      )}
      {newItemOpen && (
        <NewItemModal
          categories={allCategories}
          defaultCategory={filters.category && filters.category !== UNCATEGORISED ? filters.category : null}
          busy={busy}
          onCancel={() => setNewItemOpen(false)}
          onCreate={onCreateItem}
        />
      )}
      {addingTo && (
        <AddSlideChooser
          where={`to "${state.items.find((it) => it.slug === addingTo)?.name || addingTo}"`}
          deckSlug={addingTo}
          onDescribe={null}
          onPickTemplate={handlePickForItem}
          onUploadSlide={handleUploadForItem}
          onClose={() => {
            setAddingTo(null);
            refresh();
          }}
          busy={busy}
        />
      )}
      {editing && editingFirstSlide && (
        <SlideEditorOverlay
          item={editing}
          slide={editingFirstSlide}
          onClose={() => {
            setEditingItem(null);
            refresh();
          }}
        />
      )}
      {zoomSlide && (
        <SlidePreviewModal
          slug={zoomSlide.slug}
          slideIdx={zoomSlide.idx}
          title={zoomSlide.title}
          slideIdxs={zoomSlide.idxs}
          onClose={() => setZoomSlide(null)}
        />
      )}
    </div>
  );
}

type NewItemModalProps = {
  categories: string[];
  /** Pre-selects the shelf the user is already browsing; null falls through to
   *  the first category. */
  defaultCategory?: string | null;
  busy?: boolean;
  onCancel: () => void;
  onCreate: (name: string, category: string) => void;
};

/** Name + category for a brand-new library item; the first slide gets picked
 *  straight after via the Add-a-slide chooser. */
function NewItemModal({ categories, defaultCategory, busy, onCancel, onCreate }: NewItemModalProps) {
  const [name, setName] = useState('');
  const [category, setCategory] = useState(defaultCategory || categories[0] || '');
  const [newCategory, setNewCategory] = useState('');
  const nameRef = React.useRef<HTMLInputElement>(null);

  useEffect(() => {
    nameRef.current?.focus();
    function onKey(e: KeyboardEvent) {
      if (e.key === 'Escape') onCancel();
    }
    document.addEventListener('keydown', onKey);
    return () => document.removeEventListener('keydown', onKey);
  }, [onCancel]);

  const resolvedCategory = category === '__new' ? newCategory.trim() : category;
  const canCreate = !busy && name.trim().length > 0 && (resolvedCategory.length > 0 || categories.length === 0);

  return (
    <div className="modal-backdrop" onMouseDown={onCancel}>
      <form
        className="modal modal--confirm"
        role="dialog"
        aria-modal="true"
        aria-labelledby="new-item-title"
        onMouseDown={(e) => e.stopPropagation()}
        onSubmit={(e) => {
          e.preventDefault();
          if (canCreate) onCreate(name.trim(), resolvedCategory || 'Library');
        }}
      >
        <header className="modal__header">
          <h3 className="modal__title modal__title--sm" id="new-item-title">
            New library item
          </h3>
        </header>
        <div className="modal__body">
          <div className="save-library__choices">
            <label className="save-library__choice">
              <span>Name:</span>
              <input
                ref={nameRef}
                type="text"
                className="save-library__new"
                value={name}
                placeholder='e.g. "Case study: Morgan Sindall"'
                onChange={(e) => setName(e.target.value)}
                disabled={busy}
              />
            </label>
            <label className="save-library__choice">
              <span>Category:</span>
              <select
                className="save-library__new"
                value={category}
                onChange={(e) => setCategory(e.target.value)}
                disabled={busy}
              >
                {categories.map((c) => (
                  <option key={c} value={c}>
                    {c}
                  </option>
                ))}
                <option value="__new">New category…</option>
              </select>
            </label>
            {category === '__new' && (
              <input
                type="text"
                className="save-library__new save-library__new--indent"
                value={newCategory}
                placeholder="Category: e.g. Case studies, or Case studies/Acme"
                onChange={(e) => setNewCategory(e.target.value)}
                disabled={busy}
              />
            )}
          </div>
          <p className="muted small">
            An item is self-contained: one slide or a series. You'll pick its first slide next; add more from the item's
            ⋮ menu to build a series.
          </p>
        </div>
        <footer className="modal__footer">
          <button type="button" className="btn btn--ghost" onClick={onCancel} disabled={busy}>
            Cancel
          </button>
          <button type="submit" className="btn btn--primary" disabled={!canCreate}>
            Create item
          </button>
        </footer>
      </form>
    </div>
  );
}
